// Helpers for "sort into groups" answers (see SortAnswer.tsx).

import { isSortId, sortEntry } from '../../platform/content/sortAnswer';

export type SortOption = { id: string; label: string };
export type SortQuestion = { groups: SortOption[]; items: SortOption[] };

function readSortList(
  list: unknown,
  min: number,
  max: number,
): SortOption[] | null {
  if (!Array.isArray(list) || list.length < min || list.length > max)
    return null;
  const valid = list.filter(
    (entry): entry is SortOption =>
      typeof entry === 'object' &&
      entry !== null &&
      isSortId((entry as Record<string, unknown>).id) &&
      typeof (entry as Record<string, unknown>).label === 'string',
  );
  if (valid.length !== list.length) return null;
  return new Set(valid.map((entry) => entry.id)).size === valid.length
    ? valid
    : null;
}

// The item's own groups and items, read defensively: 2 or 3 groups and 4 to
// 8 items, each with a single lower-case letter for its id, no letter used
// twice in the same list. (A group may share its letter with an item: they
// are separate lists.)
export function readSort(content: unknown): SortQuestion | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  const groups = readSortList(record.groups, 2, 3);
  const items = readSortList(record.items, 4, 8);
  return groups && items ? { groups, items } : null;
}

// The practice screen keeps every answer as one piece of text. For sorting
// that is one line per sorted item, "item=group"; an item not sorted yet has
// no line. Only this question's own items and groups are read back.
export function splitSortChoices(
  question: SortQuestion,
  value: string,
): Record<string, string> {
  const choices: Record<string, string> = {};
  for (const line of value.split('\n')) {
    const cut = line.indexOf('=');
    if (cut <= 0) continue;
    const itemId = line.slice(0, cut);
    const groupId = line.slice(cut + 1);
    if (
      question.items.some((item) => item.id === itemId) &&
      question.groups.some((group) => group.id === groupId)
    )
      choices[itemId] = groupId;
  }
  return choices;
}

// Empty text while nothing is sorted, so "nothing answered yet" reads the
// same as it does for every other kind of question.
export function joinSortChoices(
  question: SortQuestion,
  choices: Record<string, string>,
): string {
  return question.items
    .flatMap((item) => {
      const groupId = choices[item.id];
      return groupId === undefined ? [] : [`${item.id}=${groupId}`];
    })
    .join('\n');
}

// How many items are in a group so far.
export function sortedCount(question: SortQuestion, value: string): number {
  return Object.keys(splitSortChoices(question, value)).length;
}

// What is sent: one "<item id>=<group id>" for each item, in the items' own
// order. Null until every item is in a group.
export function sortOrder(
  question: SortQuestion,
  value: string,
): string[] | null {
  const choices = splitSortChoices(question, value);
  const order: string[] = [];
  for (const item of question.items) {
    const entry = sortEntry(item.id, choices[item.id] ?? '');
    if (entry === null) return null;
    order.push(entry);
  }
  return order;
}
